/* iter134b · Listing-Attach-Picker mit Bereichs-Filter + Gruppierung.
 * Zwei-Tab-UI (Alle / Marketplace / stilltouchedX) für bessere Übersicht.
 * Max 3 MP/STX-Listings pro Post.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { X, Plus, Store, Package, Filter, Wand2 } from "lucide-react";
import api from "@/lib/api";

const MAX = 3;

const FILTERS = [
  { key: "ALL", label: "Alle" },
  { key: "MARKETPLACE", label: "Marketplace", icon: Store },
  { key: "STILLTOUCHEDX", label: "stilltouchedX", icon: Package },
];

export default function ListingAttachPicker({ selectedIds = [], onChange, area = null, testIdPrefix = "listing-attach" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(area || "ALL");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/seller/listings");
        let list = (data.listings || []).filter((l) => l.listing_status === "PUBLISHED");
        // P4-UI (§3.6 / §7.6): im bereichsgebundenen Composer nur Artikel des
        // passenden Bereichs — Social → MARKETPLACE, stilltouchedX → STILLTOUCHEDX.
        if (area) list = list.filter((l) => l.area_scope === area);
        setListings(list);
      } catch {
        setListings([]);
      } finally { setLoading(false); }
    })();
  }, [open, area]);

  const selected = listings.filter((l) => selectedIds.includes(l.listing_id));

  const grouped = useMemo(() => {
    const mp = listings.filter((l) => l.area_scope === "MARKETPLACE");
    const stx = listings.filter((l) => l.area_scope === "STILLTOUCHEDX");
    return { MARKETPLACE: mp, STILLTOUCHEDX: stx };
  }, [listings]);

  const visibleSections = area
    ? [area].filter((k) => grouped[k].length > 0)
    : filter === "ALL"
      ? ["STILLTOUCHEDX", "MARKETPLACE"].filter((k) => grouped[k].length > 0)
      : [filter].filter((k) => grouped[k].length > 0);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else if (selectedIds.length < MAX) onChange([...selectedIds, id]);
  };

  // Zum Artikel-Wizard: offenes „Beitrag erstellen"-Fenster schließen (globaler Composer)
  // und in den Wizard navigieren — bereichsabhängig (Social/MP vs. STX).
  const goToWizard = () => {
    const feedArea = area === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "SOCIAL";
    const listingArea = area === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "MARKETPLACE";
    try {
      window.dispatchEvent(new CustomEvent("composer-save-draft"));
      window.dispatchEvent(new CustomEvent("close-feed-composer"));
    } catch { /* ignore */ }
    setOpen(false);
    navigate(`/listing/new?area=${listingArea}&return=composer&composer_area=${feedArea}`);
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">Artikel verlinken (optional · max {MAX})</label>
      <div className="flex flex-wrap gap-2">
        {selected.map((l) => (
          <div key={l.listing_id} data-testid={`${testIdPrefix}-chip-${l.listing_id}`} className="flex items-center gap-2 rounded-full border border-[#E5E5E5] pl-2 pr-1 py-1 bg-white">
            {l.area_scope === "STILLTOUCHEDX" ? <Package size={12} /> : <Store size={12} />}
            <span className="text-xs truncate max-w-[140px]">{l.title}</span>
            <button
              type="button"
              onClick={() => toggle(l.listing_id)}
              data-testid={`${testIdPrefix}-remove-${l.listing_id}`}
              className="h-6 w-6 rounded-full flex items-center justify-center text-[#999] hover:bg-[#F5F5F5]"
              aria-label="Entfernen"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {selectedIds.length < MAX && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-add`}
            onClick={() => setOpen(true)}
            className="flex items-center gap-1 rounded-full border border-dashed border-[#CCC] px-3 py-1 text-xs text-[#666] hover:bg-[#FAFAFA]"
          >
            <Plus size={12} /> Artikel hinzufügen
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <p className="text-sm font-bold">Artikel auswählen ({selectedIds.length}/{MAX})</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"
                data-testid={`${testIdPrefix}-close`}
              >
                <X size={16} />
              </button>
            </div>

            {/* iter134b: Bereichs-Filter (Pill-Tabs) — ausgeblendet, wenn der
                Composer bereits an einen Bereich gebunden ist (Social/STX). */}
            {!area && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E5E5] overflow-x-auto">
              <Filter size={12} className="text-[#999] shrink-0" />
              {FILTERS.map((f) => {
                const active = filter === f.key;
                const count = f.key === "ALL" ? listings.length : (grouped[f.key]?.length || 0);
                const Icon = f.icon;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    data-testid={`${testIdPrefix}-filter-${f.key}`}
                    className={`shrink-0 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666] hover:bg-[#EEEEEE]"}`}
                  >
                    {Icon && <Icon size={11} />}
                    {f.label}
                    <span className={`text-[10px] ${active ? "text-white/70" : "text-[#999]"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            )}

            <div className="overflow-y-auto flex-1">
              {loading && <p className="p-4 text-xs text-[#999]">Lade …</p>}
              {!loading && visibleSections.length === 0 && (
                <div className="p-6 text-center" data-testid={`${testIdPrefix}-empty`}>
                  <Package size={28} className="mx-auto text-[#CCC]" />
                  <p className="mt-2 text-sm text-[#666]">{t("listing_editor_extra.no_active_listings_in_area")}</p>
                  <p className="mt-1 text-xs text-[#999]">Erstelle zuerst einen Artikel im Artikel-Wizard, dann kannst du ihn hier verlinken.</p>
                </div>
              )}
              {!loading && visibleSections.map((areaKey) => {
                const items = grouped[areaKey];
                const isSTX = areaKey === "STILLTOUCHEDX";
                return (
                  <div key={areaKey} data-testid={`${testIdPrefix}-section-${areaKey}`}>
                    <div className="sticky top-0 flex items-center gap-2 px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      {isSTX ? <Package size={12} /> : <Store size={12} />}
                      <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]">
                        {isSTX ? "stilltouchedX" : "Marketplace"}
                      </h3>
                      <span className="text-[10px] text-[#999]">· {items.length}</span>
                    </div>
                    <div className="divide-y divide-[#F0F0F0]">
                      {items.map((l) => {
                        const isSel = selectedIds.includes(l.listing_id);
                        const canPick = isSel || selectedIds.length < MAX;
                        return (
                          <button
                            key={l.listing_id}
                            type="button"
                            disabled={!canPick}
                            onClick={() => toggle(l.listing_id)}
                            data-testid={`${testIdPrefix}-option-${l.listing_id}`}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left ${isSel ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"} disabled:opacity-40 transition-colors`}
                          >
                            <div className="w-11 h-11 shrink-0 rounded-xl overflow-hidden bg-[#F5F5F5]">
                              {(l.media_urls?.[0] || l.photo_urls?.[0]) && (
                                <img src={l.media_urls?.[0] || l.photo_urls?.[0]} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{l.title}</p>
                              <p className="text-[11px] text-[#666]">
                                {Number(l.price || 0).toFixed(2)} {l.currency || "EUR"}
                              </p>
                            </div>
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isSel ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#999]"}`}>
                              {isSel ? "✓" : "+"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-[#E5E5E5] px-4 py-3 space-y-2">
              <button
                type="button"
                data-testid={`${testIdPrefix}-goto-wizard-footer`}
                onClick={goToWizard}
                className="w-full h-10 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F5F5] inline-flex items-center justify-center gap-1.5"
              >
                <Wand2 size={15} /> Neuen Artikel im Wizard erstellen
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testIdPrefix}-done`}
                className="w-full h-10 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222]"
              >
                Fertig ({selectedIds.length}/{MAX})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
